import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { apiError } from '../../services/api';
import { useApi } from '../../hooks/useApi';
import Select from '../../components/ui/Select';
import StatusPill from '../../components/StatusPill';
import { CodeChip } from '../../components/ui/CountrySelect';
import { Card, Field, LoadingRows, Notice, PageHeader, Segmented } from '../../components/ui/Page';
import { date, money, moneyMajor, titleCase } from '../../lib/format';
import { DEFAULT_CODE, taxLabel } from '../Fees';
import { KIND_LABEL, METHOD_LABEL, statusOf, type PaymentsResponse } from './index';

const today = () => new Date().toISOString().slice(0, 10);

export default function PaymentFormPage() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const presetPayment = search.get('paymentId');
  const { data, loading, error: loadError } = useApi<PaymentsResponse>('/admin/payments');

  const [companyId, setCompanyId] = useState('');
  const [target, setTarget] = useState<string>('new');
  const [form, setForm] = useState({
    kind: 'RATING_FEE' as 'RATING_FEE' | 'SUCCESS_FEE',
    fee: '',
    currency: 'INR',
    description: '',
    received: true,
    method: 'BANK_TRANSFER',
    reference: '',
    receivedAt: today(),
    note: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const companies = useMemo(() => data?.companies || [], [data]);
  const company = companies.find((c) => c.id === companyId) || null;
  const openInvoices = useMemo(
    () => (data?.payments || []).filter((p) => p.company.id === companyId && (p.status === 'PENDING' || p.status === 'FAILED')),
    [data, companyId],
  );
  const ratingPaid = (data?.payments || []).some((p) => p.company.id === companyId && p.kind === 'RATING_FEE' && p.status === 'PAID');
  const schedule = useMemo(() => {
    if (!company) return null;
    const rows = data?.schedules || [];
    return rows.find((s) => s.countryCode === company.countryCode) || rows.find((s) => s.countryCode === DEFAULT_CODE) || null;
  }, [company, data]);

  useEffect(() => {
    if (!data || !presetPayment) return;
    const p = data.payments.find((x) => x.id === presetPayment);
    if (p) {
      setCompanyId(p.company.id);
      setTarget(p.id);
    }
  }, [data, presetPayment]);

  const pickCompany = (id: string) => {
    setCompanyId(id);
    const open = (data?.payments || []).filter((p) => p.company.id === id && (p.status === 'PENDING' || p.status === 'FAILED'));
    setTarget(open[0]?.id || 'new');
    const c = companies.find((x) => x.id === id);
    const rows = data?.schedules || [];
    const s = c ? rows.find((x) => x.countryCode === c.countryCode) || rows.find((x) => x.countryCode === DEFAULT_CODE) : null;
    const alreadyRated = (data?.payments || []).some((p) => p.company.id === id && p.kind === 'RATING_FEE' && p.status !== 'CANCELLED');
    setForm((f) => ({
      ...f,
      currency: s?.currency || f.currency,
      kind: alreadyRated ? 'SUCCESS_FEE' : 'RATING_FEE',
      fee: !alreadyRated && s?.ratingFee ? String(s.ratingFee / 100) : '',
    }));
  };

  const invoice = openInvoices.find((p) => p.id === target) || null;
  const fee = Number(form.fee || 0);
  const pct = schedule?.taxPercent || 0;
  const preview =
    pct && fee
      ? schedule!.taxInclusive
        ? { subtotal: fee / (1 + pct / 100), tax: fee - fee / (1 + pct / 100), total: fee }
        : { subtotal: fee, tax: (fee * pct) / 100, total: fee * (1 + pct / 100) }
      : { subtotal: fee, tax: 0, total: fee };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!company) {
      setError('Choose the company that paid.');
      return;
    }
    setSaving(true);
    setError(null);
    const receipt = { method: form.method, reference: form.reference || null, receivedAt: form.receivedAt || null, note: form.note || null };
    try {
      if (invoice) {
        await api.post('/admin/payments', { companyId, paymentId: invoice.id, ...receipt });
      } else {
        await api.post('/admin/payments', {
          companyId,
          kind: form.kind,
          fee,
          currency: form.currency,
          description: form.description || null,
          status: form.received ? 'PAID' : 'PENDING',
          ...receipt,
        });
      }
      const text = invoice
        ? `Receipt recorded — ${invoice.invoiceNumber} for ${company.name} is paid.`
        : form.received
          ? `Payment of ${moneyMajor(preview.total, form.currency)} from ${company.name} added to the ledger.`
          : `Invoice of ${moneyMajor(preview.total, form.currency)} raised for ${company.name}. They can pay it from their workspace.`;
      navigate('/payments', { replace: true, state: { notice: text } });
    } catch (err) {
      setError(apiError(err, 'Could not save the payment.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) return <LoadingRows rows={6} />;
  if (!data) return <Notice tone="error">{loadError || 'Could not load payments.'}</Notice>;

  const showReceipt = Boolean(invoice) || form.received;

  return (
    <div>
      <PageHeader
        crumbs={[{ label: 'Payments', to: '/payments' }, { label: 'Create payment' }]}
        title="Create payment"
        description="Record money a company transferred to Micus and ledger it — against an open invoice, or as a new payment. You can also raise an invoice the company pays later."
      />
      <form onSubmit={onSubmit} className="grid gap-4 lg:grid-cols-[1.3fr_1fr]">
        <div className="space-y-4">
          <Card title="Company">
            <Select
              value={companyId}
              onChange={pickCompany}
              searchable
              placeholder="Choose the company that paid"
              options={companies.map((c) => ({
                value: c.id,
                label: c.name,
                description: `${c.legalName && c.legalName !== c.name ? `${c.legalName} · ` : ''}${titleCase(c.status)}`,
                prefix: <CodeChip code={c.countryCode} />,
                keywords: c.legalName || undefined,
              }))}
            />
            {company && (
              <div className="mt-4">
                <p className="mz-label">Apply this payment to</p>
                <div className="grid gap-2">
                  {openInvoices.map((p) => (
                    <label key={p.id} className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 text-[13px] transition ${target === p.id ? 'border-[#d6006e] bg-[#fdf2f8]' : 'border-[#f0cfdd] bg-white hover:bg-[#fdf6f9]'}`}>
                      <input type="radio" name="target" className="accent-[#d6006e]" checked={target === p.id} onChange={() => setTarget(p.id)} />
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold">
                          {KIND_LABEL[p.kind]} · <span className="font-mono text-[12px]">{p.invoiceNumber}</span>
                        </p>
                        <p className="truncate text-xs text-[#a77d92]">
                          {p.description} · raised {date(p.createdAt)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">{money(p.amount, p.currency)}</p>
                        <StatusPill label={statusOf(p).label} tone={statusOf(p).tone} />
                      </div>
                    </label>
                  ))}
                  <label className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 text-[13px] transition ${target === 'new' ? 'border-[#d6006e] bg-[#fdf2f8]' : 'border-[#f0cfdd] bg-white hover:bg-[#fdf6f9]'}`}>
                    <input type="radio" name="target" className="accent-[#d6006e]" checked={target === 'new'} onChange={() => setTarget('new')} />
                    <div>
                      <p className="font-semibold">New payment</p>
                      <p className="text-xs text-[#a77d92]">{openInvoices.length ? 'Not for an invoice above — ledger a separate payment.' : 'This company has no open invoices.'}</p>
                    </div>
                  </label>
                </div>
              </div>
            )}
          </Card>

          {company && !invoice && (
            <Card title="Payment">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Fee" className="sm:col-span-2">
                  <Segmented
                    value={form.kind}
                    onChange={(v) => setForm((f) => ({ ...f, kind: v }))}
                    options={[
                      { value: 'RATING_FEE', label: 'Rating fee · before rating' },
                      { value: 'SUCCESS_FEE', label: 'Success fee · after funding' },
                    ]}
                  />
                  {form.kind === 'RATING_FEE' && ratingPaid && <p className="mz-hint text-[#b45309]">This company has already paid its rating fee.</p>}
                </Field>
                <Field label={schedule?.taxInclusive && pct ? `Amount (incl. ${schedule.taxName})` : 'Fee before tax'} htmlFor="p-fee">
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-[#a77d92]">{form.currency}</span>
                    <input id="p-fee" required type="number" min="0.01" step="0.01" className="mz-input pl-12" value={form.fee} onChange={(e) => setForm((f) => ({ ...f, fee: e.target.value }))} />
                  </div>
                </Field>
                <Field label="Currency" htmlFor="p-cur">
                  <Select id="p-cur" value={form.currency} onChange={(v) => setForm((f) => ({ ...f, currency: v }))} options={data.currencies.map((c) => ({ value: c, label: c }))} />
                </Field>
                <Field label="Description" htmlFor="p-desc" className="sm:col-span-2" hint="Printed on the invoice. Leave blank for the standard wording.">
                  <input
                    id="p-desc"
                    maxLength={200}
                    className="mz-input"
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder={form.kind === 'RATING_FEE' ? 'Micus credit rating fee' : 'Micus success fee'}
                  />
                </Field>
                <Field label="Status" className="sm:col-span-2">
                  <Segmented
                    value={form.received ? 'yes' : 'no'}
                    onChange={(v) => setForm((f) => ({ ...f, received: v === 'yes' }))}
                    options={[
                      { value: 'yes', label: 'Money received' },
                      { value: 'no', label: 'Raise invoice · awaiting transfer' },
                    ]}
                  />
                </Field>
              </div>
            </Card>
          )}

          {company && showReceipt && (
            <Card title="Receipt" subtitle="How the money reached Micus.">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Method" htmlFor="p-method">
                  <Select id="p-method" value={form.method} onChange={(v) => setForm((f) => ({ ...f, method: v }))} options={data.methods.map((m) => ({ value: m, label: METHOD_LABEL[m] || m }))} />
                </Field>
                <Field label="Received on" htmlFor="p-date">
                  <input id="p-date" type="date" required max={today()} className="mz-input" value={form.receivedAt} onChange={(e) => setForm((f) => ({ ...f, receivedAt: e.target.value }))} />
                </Field>
                <Field label="Reference" htmlFor="p-ref" className="sm:col-span-2" hint="UTR, bank reference or cheque number — printed on the invoice.">
                  <input id="p-ref" maxLength={120} className="mz-input font-mono text-[13px]" value={form.reference} onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} />
                </Field>
                <Field label="Note" htmlFor="p-note" className="sm:col-span-2">
                  <textarea id="p-note" rows={2} maxLength={1000} className="mz-input" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
                </Field>
              </div>
            </Card>
          )}

          {error && <Notice tone="error">{error}</Notice>}
          <div className="mz-card sticky bottom-3 flex flex-wrap items-center justify-end gap-2 px-4 py-3">
            <button type="button" className="mz-btn-ghost" onClick={() => navigate('/payments')}>
              Cancel
            </button>
            <button type="submit" disabled={saving || !company} className="mz-btn-primary">
              {saving ? 'Saving…' : invoice ? 'Record receipt' : form.received ? 'Add to ledger' : 'Raise invoice'}
            </button>
          </div>
        </div>

        <div className="space-y-4 lg:sticky lg:top-6 lg:self-start">
          <div className="overflow-hidden rounded-2xl text-white" style={{ background: 'linear-gradient(145deg, #2b0018 0%, #7a0038 50%, #d6006e 100%)', boxShadow: '0 18px 44px rgba(157,0,80,0.25)' }}>
            <div className="p-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/70">{invoice ? 'Settling invoice' : 'Ledger entry'}</p>
              <p className="mt-2 text-lg font-bold">{company?.name || 'Choose a company'}</p>
              {invoice ? (
                <>
                  <p className="mt-3 text-4xl font-extrabold tracking-tight">{money(invoice.amount, invoice.currency)}</p>
                  <p className="mt-1 text-xs text-white/70">
                    {invoice.invoiceNumber} · {KIND_LABEL[invoice.kind]}
                    {invoice.taxAmount > 0 && ` · incl. ${invoice.taxName} ${money(invoice.taxAmount, invoice.currency)}`}
                  </p>
                </>
              ) : (
                <>
                  <p className="mt-3 text-4xl font-extrabold tracking-tight">{moneyMajor(preview.total, form.currency)}</p>
                  <dl className="mt-3 space-y-1 text-xs text-white/80">
                    <div className="flex justify-between">
                      <dt>Fee</dt>
                      <dd>{moneyMajor(preview.subtotal, form.currency)}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt>{schedule ? `${schedule.taxName} ${pct}%` : 'Tax'}</dt>
                      <dd>{moneyMajor(preview.tax, form.currency)}</dd>
                    </div>
                  </dl>
                </>
              )}
            </div>
            <div className="bg-black/20 px-5 py-3 text-xs text-white/80">
              {schedule
                ? `Tax from the ${schedule.countryCode === DEFAULT_CODE ? 'international default' : schedule.countryName} schedule: ${taxLabel(schedule)}.`
                : company
                  ? 'No fee schedule applies to this country — no tax is added.'
                  : 'Tax is applied from the company’s fee schedule.'}
            </div>
          </div>
          <p className="px-1 text-xs leading-relaxed text-[#a77d92]">
            The company sees the payment in its workspace and gets a system message. A rating-fee receipt unlocks its rating report. Every entry has a downloadable invoice.
          </p>
        </div>
      </form>
    </div>
  );
}
